import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Gavel,
  Brain,
  CircleDollarSign,
  Calendar,
  FileText,
  TrendingUp,
  ShieldCheck,
  Sparkles,
  ArrowRight,
  Check,
  Users,
  Activity,
  Search,
} from "lucide-react";
import { Section, GlassCard, Eyebrow } from "../components/Section";
import NeuralNetworkBackground, { ScaleWatermark } from "../components/NeuralNetworkBackground";

const HeroBlock = () => (
  <section className="relative pt-32 pb-24 overflow-hidden">
    <div className="absolute inset-0 opacity-60"><NeuralNetworkBackground density={26} /></div>
    <ScaleWatermark />
    <div className="turquoise-aura -top-32 -left-32 opacity-60" />
    <div className="grain-overlay" />
    <div className="container relative z-10 mx-auto px-6 max-w-7xl">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-4xl">
        <div className="aj-chip mb-6"><Gavel className="w-3 h-3" /> For Lawyers</div>
        <h1 className="font-playfair text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-white leading-[1.05]">
          The legal{" "}
          <span className="italic text-aj-gradient">operating system</span>{" "}
          built for the future of practice.
        </h1>
        <p className="font-inter text-base md:text-lg text-white/65 mt-6 max-w-2xl leading-relaxed">
          Verified clients. AI productivity tools. Escrow-protected payments. Reputation that compounds.
          Everything you need to grow a modern legal practice — in one platform.
        </p>
        <div className="flex flex-wrap gap-4 mt-10">
          <Link to="/register" data-testid="lawyers-cta-join" className="btn-aj-primary">
            Apply to Join <ArrowRight className="w-4 h-4" />
          </Link>
          <Link to="/login" data-testid="lawyers-cta-login" className="btn-aj-secondary">
            Lawyer Sign In
          </Link>
        </div>
      </motion.div>
    </div>
  </section>
);

const features = [
  { icon: Brain, t: "AI Legal Research", d: "Find authorities, ratio decidendi, statutes and court rules in seconds — fully cited." },
  { icon: Sparkles, t: "AI Copilot", d: "Draft pleadings, affidavits, contracts and demand letters. You stay in control of every word." },
  { icon: Search, t: "Case Assessment", d: "Auto-generated legal issues, causes of action, evidence checklist, complexity score." },
  { icon: FileText, t: "Drafting Studio", d: "Motions, contracts, opinions — generated against your house style and jurisdiction." },
  { icon: ShieldCheck, t: "Document Review", d: "Flag risk clauses, missing clauses, contradictions and compliance issues automatically." },
  { icon: Calendar, t: "Calendar & Booking", d: "Integrated Calendly / Google Calendar. Court dates with deadline alerts." },
  { icon: CircleDollarSign, t: "Escrow Payments", d: "Milestone-based releases. Commencement → Filing → Completion. Zero off-platform leakage." },
  { icon: Users, t: "Verified Clients", d: "Pre-screened matters routed by the matching engine. Match Score on every assignment." },
  { icon: TrendingUp, t: "Reputation Engine", d: "Ratings, response time and outcomes power your visibility and referral volume." },
];

const matchingFactors = [
  { f: "Practice Area Expertise", w: "30%" },
  { f: "Geographic Proximity", w: "20%" },
  { f: "Court Jurisdiction Experience", w: "15%" },
  { f: "Availability", w: "10%" },
  { f: "Response Time", w: "10%" },
  { f: "Ratings / Client Feedback", w: "10%" },
  { f: "Language Fit", w: "5%" },
];

const paymentStages = [
  { stage: "Stage 1", title: "Commencement Fee", pct: "20–30%", desc: "Released after lawyer accepts case, consultation is held, engagement letter is signed." },
  { stage: "Stage 2", title: "Main Work / Filing Fee", pct: "50–60%", desc: "Released when lawyer begins substantive work — drafting, filing, first court appearance." },
  { stage: "Stage 3", title: "Completion Payoff", pct: "10–20%", desc: "Released after judgment, settlement, or matter conclusion with client confirmation." },
];

const ForLawyers = () => (
  <div className="bg-ink text-white" data-testid="for-lawyers-page">
    <HeroBlock />

    <Section eyebrow="What you get" title={<>Tools that respect your craft — <span className="italic text-aj-gradient">not replace it.</span></>}>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {features.map((f, i) => (
          <motion.div key={f.t} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.45, delay: i * 0.05 }}>
            <GlassCard glow className="h-full">
              <f.icon className="w-7 h-7 text-turquoise mb-4" />
              <h3 className="font-playfair text-xl text-white mb-2">{f.t}</h3>
              <p className="font-inter text-sm text-white/60 leading-relaxed">{f.d}</p>
            </GlassCard>
          </motion.div>
        ))}
      </div>
    </Section>

    {/* Matching engine */}
    <Section eyebrow="The Matching Engine" title={<>How matters reach <span className="italic text-aj-gradient">the right lawyer.</span></>} subtitle="Every legal request is ranked across seven weighted dimensions. The most suitable lawyer is notified first — with a 30-minute response window before auto-routing to the next match.">
      <div className="grid lg:grid-cols-2 gap-8">
        <div className="space-y-2.5">
          {matchingFactors.map((m, i) => (
            <motion.div
              key={m.f}
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.06 }}
              className="glass rounded-xl p-4 flex items-center justify-between hover:border-turquoise/30 transition-all"
            >
              <span className="font-inter text-sm text-white/85">{m.f}</span>
              <span className="font-playfair italic text-2xl text-gold-gradient">{m.w}</span>
            </motion.div>
          ))}
        </div>
        <GlassCard className="bg-white/[0.04]">
          <Eyebrow className="mb-4">Sample Match</Eyebrow>
          <h4 className="font-playfair text-xl text-white">Family Law · Domestic Violence · Abuja</h4>
          <div className="mt-4 space-y-2 font-inter text-sm text-white/65">
            <div>• Practice area: <span className="text-white">Family / DV</span></div>
            <div>• Location: <span className="text-white">Abuja</span></div>
            <div>• Urgency: <span className="text-gold-300">High</span></div>
            <div>• Lawyer pool: <span className="text-white">42 eligible</span></div>
            <div>• Top match: <span className="text-turquoise">Barr. Adekunle — score 94%</span></div>
          </div>
          <div className="mt-6 p-4 rounded-xl bg-turquoise/[0.05] border border-turquoise/20">
            <p className="font-cormorant italic text-base text-turquoise leading-relaxed">
              &ldquo;Top-rated lawyers receive priority routing. Build your reputation and the platform delivers.&rdquo;
            </p>
          </div>
        </GlassCard>
      </div>
    </Section>

    {/* 3-stage payments */}
    <Section eyebrow="Payment Architecture" title={<>Escrow protection. <span className="italic text-aj-gradient">Milestone release.</span> Zero leakage.</>} aurora>
      <div className="grid md:grid-cols-3 gap-5">
        {paymentStages.map((p, i) => (
          <motion.div key={p.stage} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.1 }}>
            <GlassCard glow className="h-full">
              <Eyebrow className="mb-4">{p.stage}</Eyebrow>
              <h3 className="font-playfair text-2xl text-white mb-2">{p.title}</h3>
              <div className="font-playfair italic text-5xl text-gold-gradient mb-4">{p.pct}</div>
              <p className="font-inter text-sm text-white/60 leading-relaxed">{p.desc}</p>
            </GlassCard>
          </motion.div>
        ))}
      </div>
      <p className="font-inter text-sm text-white/55 text-center mt-10 max-w-2xl mx-auto">
        AccessJustice deducts a 10% platform service fee on legal payments and provides full invoice, receipt and audit trail for every transaction.
      </p>
    </Section>

    {/* Standards */}
    <Section eyebrow="Professional Standards" title={<>NBA-grade ethics. <span className="italic text-aj-gradient">Non-negotiable.</span></>}>
      <div className="grid md:grid-cols-2 gap-6">
        <GlassCard>
          <h4 className="font-playfair text-xl text-white mb-4">Verification Required</h4>
          <ul className="space-y-2.5 font-inter text-sm text-white/70">
            {["NBA enrollment status", "Supreme Court number", "Practice areas & jurisdictions", "Years of practice", "Languages spoken", "Bank/payment wallet"].map((p) => (
              <li key={p} className="flex items-start gap-2.5">
                <Check className="w-4 h-4 text-turquoise mt-0.5 shrink-0" /> {p}
              </li>
            ))}
          </ul>
        </GlassCard>
        <GlassCard>
          <h4 className="font-playfair text-xl text-white mb-4">Grounds for Suspension</h4>
          <ul className="space-y-2.5 font-inter text-sm text-white/70">
            {[
              "Collecting payment outside the platform",
              "Failure to deliver agreed service",
              "Misrepresentation or fraud",
              "Abandoning a client",
              "Breach of confidentiality",
              "NBA disciplinary action",
            ].map((p) => (
              <li key={p} className="flex items-start gap-2.5">
                <span className="w-1.5 h-1.5 mt-2 bg-red-400 rounded-full shrink-0" /> {p}
              </li>
            ))}
          </ul>
        </GlassCard>
      </div>
    </Section>

    {/* Final CTA */}
    <Section className="border-t border-white/8">
      <div className="text-center max-w-3xl mx-auto">
        <Eyebrow>Apply today</Eyebrow>
        <h2 className="font-playfair text-4xl md:text-5xl text-white mt-4 mb-8 leading-tight">
          Join the lawyers building{" "}
          <span className="italic text-aj-gradient">the future of legal practice.</span>
        </h2>
        <div className="flex flex-wrap justify-center gap-4">
          <Link to="/register" data-testid="lawyers-final-join" className="btn-aj-primary">
            Become a Justice-AI Lawyer <ArrowRight className="w-4 h-4" />
          </Link>
          <Link to="/contact" data-testid="lawyers-final-contact" className="btn-aj-secondary">
            Speak to our team
          </Link>
        </div>
      </div>
    </Section>
  </div>
);

export default ForLawyers;
